// §18 F16 — two tabs of Hangar open at once.
//
// Two things have to be true and they pull in opposite directions:
//
//   • **Both tabs work.** A file written in one appears in the other without a reload, which
//     means the writer publishes its `FsEvent`s and the reader invalidates the cached handles
//     that event just made stale.
//   • **The layout has one owner.** Two tabs both persisting §5.6's session would each overwrite
//     the other's; F16 gives ownership to the **last active** tab, which is the one the user was
//     looking at.
//
// `BroadcastChannel` is the transport; the port below is narrow so both halves are testable
// without a browser, and so a browser that lacks it simply gets a single-tab desktop.
import type { FsEvent } from '@hangar/shared';

export interface ChannelPort {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  close(): void;
}

export type TabMessage =
  | { kind: 'fs'; tabId: string; event: FsEvent }
  /** "I am the tab the user is looking at." Sent when a tab becomes visible (F16). */
  | { kind: 'claim'; tabId: string; at: number };

export function isTabMessage(value: unknown): value is TabMessage {
  if (typeof value !== 'object' || value === null) return false;
  const shaped = value as { kind?: unknown; tabId?: unknown };
  if (typeof shaped.tabId !== 'string') return false;
  return shaped.kind === 'fs' || shaped.kind === 'claim';
}

export interface TabLink {
  /** Publish a local filesystem change to the other tabs. */
  publish(event: FsEvent): void;
  /** Announce that this tab is the active one (F16). */
  claim(at: number): void;
  /** Is this tab the layout owner? True until another tab claims more recently. */
  isOwner(): boolean;
  readonly tabId: string;
  close(): void;
}

export interface TabLinkOptions {
  tabId: string;
  channel: ChannelPort | null;
  /** A change another tab made. The caller invalidates its caches and re-emits it locally. */
  onRemoteChange: (event: FsEvent) => void;
  /** Called when ownership changes, so a service can stop writing (F16). */
  onOwnerChange?: (owner: boolean) => void;
}

export function createTabLink(options: TabLinkOptions): TabLink {
  const { channel, tabId } = options;
  // A lone tab owns the layout by definition; the first claim from anyone else takes it away.
  let owner = true;
  let claimedAt = 0;

  const setOwner = (next: boolean): void => {
    if (owner === next) return;
    owner = next;
    options.onOwnerChange?.(next);
  };

  const listener = (event: { data: unknown }): void => {
    const message = event.data;
    if (!isTabMessage(message) || message.tabId === tabId) return;
    if (message.kind === 'fs') {
      options.onRemoteChange(message.event);
      return;
    }
    // A claim at or after ours wins: the *last* active tab owns the layout, and a tie goes to
    // the other tab because ours has already had its turn.
    if (message.at >= claimedAt) setOwner(false);
  };

  channel?.addEventListener('message', listener);

  return {
    tabId,

    publish(event) {
      channel?.postMessage({ kind: 'fs', tabId, event } satisfies TabMessage);
    },

    claim(at) {
      claimedAt = at;
      setOwner(true);
      channel?.postMessage({ kind: 'claim', tabId, at } satisfies TabMessage);
    },

    isOwner: () => owner,

    close() {
      channel?.removeEventListener('message', listener);
      channel?.close();
    },
  };
}

/** A `BroadcastChannel`, or `null` where the browser has none (D-042 — everywhere degrades). */
export function openChannel(name: string): ChannelPort | null {
  if (typeof BroadcastChannel !== 'function') return null;
  return new BroadcastChannel(name) as unknown as ChannelPort;
}
